/*
 * Carbisdale - SeparatorInputBridge
 *
 * Kapselt die Separator-Titel-Bearbeitung: aktiviert den SingleLineInput
 * mit dem aktuellen Separator-Text, pflegt das aktive Edit-Pane
 * (main/outline/context) und verarbeitet die Callbacks des SingleLineInput
 * (Title-Change, Enter, Tab/Shift-Tab, Arrow-Navigation raus).
 *
 * Rolle wurde aus EditorInput.js extrahiert, um die Facade dort schlank
 * zu halten. EditorInput haelt eine Instanz als Kollaborateur und ruft
 * bei EinigeOperationen wie _pingBlocked / _moveVertical direkt zurueck.
 */

import { EDITOR } from '../../config.js'

export class SeparatorInputBridge {
    /**
     * @param {import('../EditorInput.js').EditorInput} host
     * @param {import('../../SingleLineInput.js').SingleLineInput} separatorInput
     */
    constructor(host, separatorInput) {
        this._host = host
        this._input = separatorInput

        // Pane-Quelle der Separator-Bearbeitung ('main' | 'outline' | 'context')
        this._editPane = 'main'
    }

    get model() { return this._host.model }
    get view() { return this._host.view }

    /** Aktives Pane fuer Separator-Bearbeitung. */
    get editPane() { return this._editPane }
    set editPane(value) { this._editPane = value }

    /**
     * Aktiviert die Separator-Eingabe fuer den aktuellen Separator.
     * Synchronisiert SingleLineInput mit dem aktuellen Separator-Text.
     * @param {number} [charOffset] - Optionale Cursor-Position im Titel
     * @param {string} [pane='main'] - Pane-Quelle ('main' | 'outline' | 'context')
     */
    activate(charOffset, pane = 'main') {
        const para = this.model.getParagraph(this.model.cursor.paragraphId)
        if (!para) return
        // Background-Separator hat keinen editierbaren Titel
        if (para.outlineDepth === EDITOR.BACKGROUND_SEPARATOR_DEPTH) return
        this._editPane = pane
        this._input.setText(para.text)
        // Sichtbare Breite abhaengig vom Pane: Main nutzt view.width,
        // Outline/Context nutzen die jeweilige Pane-Breite.
        let visWidth
        if (pane === 'outline') {
            visWidth = this.view.outlinePaneWidth - 6
        } else if (pane === 'context') {
            visWidth = this.view.contextPaneWidth - 6
        } else {
            visWidth = this.view.width - 6  // Main: Platz fuer '── ' und ' ──'
        }
        this._input.setVisibleWidth(Math.max(4, visWidth))
        if (charOffset !== undefined) {
            // Cursor direkt setzen (nicht handleMouseDown — das ist fuer Mausklicks).
            // setText() oben hat Cursor ans Ende gesetzt + scrollOffset darauf
            // eingestellt; nach dem manuellen Cursor-Reset muss _ensureCursorVisible
            // nochmal laufen, sonst bleibt der scrollOffset fuer das Ende stehen
            // waehrend der Cursor bei 0 sitzt — Label wirkt gekuerzt obwohl der
            // Cursor am Anfang steht.
            this._input.cursorPos = Math.min(charOffset, para.text.length)
            this._input.selectionStart = null
            this._input.selectionEnd = null
            this._input._ensureCursorVisible()
        }
    }

    /**
     * Callback: Separator-Titel wurde geaendert.
     */
    onTitleChange(text) {
        if (!this.model.cursor) return
        this.model.setSeparatorTitle(this.model.cursor.paragraphId, text)
        this.view.markDirty()
    }

    /**
     * Callback: Enter auf Separator.
     * Position 0: neuen Separator oberhalb einfuegen (Umbruch am Anfang).
     * Position > 0: neuen Separator unterhalb einfuegen, Cursor auf neues Label.
     */
    onEnter() {
        if (!this.model.cursor) return
        const paraIndex = this.model.getParagraphIndex(this.model.cursor.paragraphId)
        if (paraIndex < 0) return
        const para = this.model.paragraphs[paraIndex]

        if (this._input.cursorPos === 0) {
            // Position 0: neuer Separator oberhalb (bisheriges Verhalten)
            this.model.insertSeparatorBefore(paraIndex, para.outlineDepth)
        } else {
            // Position > 0: neuer Separator unterhalb, Cursor auf neues Label
            const { separatorId } = this.model.insertSeparatorAfter(paraIndex, para.outlineDepth)
            if (separatorId != null) {
                this.model.cursor.paragraphId = separatorId
                this.model.cursor.offset = 0
                this.activate(0, this._editPane)
            }
        }
        this.view.ensureCursorVisible()
    }

    /**
     * Callback: Tab auf Separator → Gliederungstiefe erhoehen.
     */
    onTab() {
        if (!this.model.cursor) return
        const para = this.model.getParagraph(this.model.cursor.paragraphId)
        if (!para) return
        this.model._actionBlocked = false
        this.model.setSeparatorDepth(para.id, para.outlineDepth + 1)
        if (this.model._actionBlocked) this._host._pingBlocked()
    }

    /**
     * Callback: Shift+Tab auf Separator → Gliederungstiefe verringern.
     */
    onShiftTab() {
        if (!this.model.cursor) return
        const para = this.model.getParagraph(this.model.cursor.paragraphId)
        if (!para) return
        this.model._actionBlocked = false
        this.model.setSeparatorDepth(para.id, para.outlineDepth - 1)
        if (this.model._actionBlocked) this._host._pingBlocked()
    }

    /**
     * Ziel-Feld beim Verlassen eines Separator-Labels.
     * Im maximierten Context/Outline muss der Cursor im sichtbaren Pane-Feld
     * landen (info bzw. note), sonst wuerde er im unsichtbaren text-Feld
     * eines Nicht-Separator-Absatzes verschwinden.
     * @returns {'text'|'note'|'info'}
     */
    _exitField() {
        const filterMode = this.view._paneFilterMode
        if (filterMode === 'context') return 'info'
        if (filterMode === 'outline') return 'note'
        return 'text'
    }

    /**
     * Pane fuer activate() bei Arrow-Nav vom Nachbar-Feld auf einen Separator.
     * Der Pane-Wert landet in sepState.pane und steuert in main.js welches
     * Pane den Cursor rendert. Ohne richtigen Pane bleibt der Cursor auf
     * dem Separator unsichtbar, sobald Main nicht sichtbar ist.
     *
     * Regel:
     *  - Main unsichtbar (filterMode aktiv) → das sichtbare Filter-Pane gewinnt
     *  - Main sichtbar → Pane aus dem pre-move-Feld (info/note/text)
     *
     * @param {'text'|'note'|'info'} previousField - cursor.field vor dem Move
     * @returns {'main'|'outline'|'context'}
     */
    editPaneForField(previousField) {
        const filterMode = this.view._paneFilterMode
        if (filterMode === 'context') return 'context'
        if (filterMode === 'outline') return 'outline'
        // filterMode === 'both' oder null: Pane aus Feld ableiten
        if (previousField === 'info') return 'context'
        if (previousField === 'note') return 'outline'
        return 'main'
    }

    /**
     * Callback: Arrow-Keys auf Separator → zum Nachbar-Absatz navigieren.
     *
     * 'up'/'down': Feld-Wechsel entsprechend Pane-Filter, dann vertikale
     *   Navigation via host._moveVertical (filter-bewusst, Goal Column pro Feld).
     * 'left'/'right': zum vorherigen/naechsten SICHTBAREN Nicht-Separator-
     *   Absatz (Separatoren werden uebersprungen, da der User sonst nur von
     *   Label zu Label springen wuerde). Ziel-Feld aus Pane-Filter.
     */
    onNavigateOut(direction) {
        if (!this.model.cursor) return

        // Horizontal: ArrowLeft am Anfang / ArrowRight am Ende des Labels
        if (direction === 'left' || direction === 'right') {
            const dir = direction === 'left' ? -1 : 1
            const targetField = this._exitField()
            // Separatoren ueberspringen — sonst wuerde Arrow-Right am Label-Ende
            // auf dem naechsten Separator landen statt im gewuenschten Info/Note.
            // Provisorische Passive duerfen Ziel sein; im note-Feld werden sie
            // beim Landen temporaer aktiviert (unten).
            const target = this.view.findVisibleNeighbor(
                this.model.cursor.paragraphId, dir, { skipSeparators: true })
            if (!target) return
            const targetText = this.model._getFieldText(target, targetField) || ''
            const offset = direction === 'left' ? targetText.length : 0
            this.model.setCursor(target.id, offset, targetField)
            this._host._resetGoalColumn()
            this._host._checkParagraphChange()
            // Temp-Aktivierung: Separator-Exit ins note-Feld eines Passiven aktiviert
            // ihn temporaer (Filter-Mode). Ohne Eingabe revertet der naechste
            // Paragraph-Wechsel.
            if (targetField === 'note' && this.view._paneFilterMode !== null
                    && !target.isOutlineItem()) {
                this._host._activateTemporarily(target)
            }
            this.view.ensureCursorVisible()
            return
        }

        // Vertikal: ArrowUp / ArrowDown
        // Feld aus Pane-Filter — ohne dies wuerde _moveVertical im text-Feld
        // bleiben und der Cursor im Context/Outline-Pane unsichtbar werden.
        const exitField = this._exitField()
        if (exitField !== this.model.cursor.field) {
            this.model.cursor.field = exitField
            this.model._markCursorDirty()
        }
        // _moveVertical initialisiert Goal Column pro Feld selbst (falls null)
        this._host._moveVertical(direction === 'up' ? -1 : 1)
        this._host._checkParagraphChange()
        this.view.ensureCursorVisible()
    }
}
